import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Target, BarChart3, Slack, Quote, ChevronLeft, ChevronRight, Loader2, ShieldCheck, ShoppingBag, Users, PlayCircle } from 'lucide-react';
import { usePublic } from '../../context/PublicContext';
import axios from 'axios';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';


const FeatureSection = ({ title, description, image, reversed = false, bgColor = "bg-white" }) => (
  <section className={`py-10 px-6 md:px-10 lg:px-12 ${bgColor}`}>
    <div className={`max-w-[1400px] mx-auto flex flex-col ${reversed ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-12`}>
      <motion.div 
        initial={{ opacity: 0, x: reversed ? 50 : -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="flex-1 space-y-6"
      >
        <h2 className="text-3xl md:text-5xl font-bold text-[#1a1a1a] leading-tight">
          {title}
        </h2>
        <p className="text-slate-600 text-lg leading-relaxed max-w-lg">
          {description}
        </p>
        <Link to="/pricing" className="inline-block bg-[#10b981] text-white px-8 py-3 rounded-full font-bold hover:bg-[#059669] transition-all flex items-center gap-2 w-fit">
          Explore Feature <ArrowRight size={18} />
        </Link>
      </motion.div>
      
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        className="flex-1 w-full"
      >
        <div className="relative group">
          <div className="absolute -inset-4 bg-emerald-50 rounded-[2.5rem] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <img 
            src={image} 
            alt={title} 
            className="relative w-full h-auto rounded-[2rem] shadow-xl z-10 transition-transform duration-500 group-hover:scale-[1.02]"
          />
        </div>
      </motion.div>
    </div>
  </section>
);

const TestimonialCard = ({ name, location, content, isFeatured = false }) => (
  <motion.div 
    initial={{ opacity: 0, scale: 0.9 }}
    whileInView={{ opacity: 1, scale: isFeatured ? 1 : 0.9 }}
    viewport={{ once: true }}
    className={`bg-white rounded-3xl p-8 shadow-xl border border-gray-50 flex flex-col gap-6 transition-all duration-500 ${isFeatured ? 'z-10 shadow-2xl scale-100 ring-4 ring-emerald-50' : 'opacity-80 scale-95'}`}
  >
    <div className="text-emerald-500">
      <Quote size={40} fill="currentColor" />
    </div>
    <p className="text-[#5a5a5a] text-sm leading-relaxed min-h-[80px]">
      {content}
    </p>
    <div className="w-full h-px bg-gray-100 my-2"></div>
    <div className="flex items-center gap-4">
      <div>
        <h4 className="font-bold text-[#1a1a1a]">{name}</h4>
        <p className="text-gray-400 text-xs">{location}</p>
      </div>
    </div>
  </motion.div>
);

const FeaturesPage = () => {
    const { openDemoModal } = usePublic();
    const [pageData, setPageData] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/common/testimonials/?page_type=features`);
        setTestimonials(response.data);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  const logos = [
    { name: 'Coinbase', icon: <div className="font-bold text-slate-400">coinbase</div> },
    { name: 'Slack', icon: <div className="flex items-center gap-2 text-slate-400 font-bold"><Slack size={20}/> slack</div> },
    { name: 'Dropbox', icon: <div className="font-bold text-slate-400">dropbox</div> },
    { name: 'Webflow', icon: <div className="font-bold text-slate-400">webflow</div> },
    { name: 'Zoom', icon: <div className="font-bold text-slate-400">zoom</div> },
    { name: 'YouTube', icon: <div className="font-bold text-slate-400">YouTube</div> },
    { name: 'Trello', icon: <div className="font-bold text-slate-400">Trello</div> },
    { name: 'Visa', icon: <div className="italic font-black text-slate-400 text-xl tracking-tighter">VISA</div> },
    { name: 'Meet', icon: <div className="font-bold text-slate-400">Meet</div> },
  ];

  return (
    <div className="min-h-screen bg-white font-lexend overflow-x-hidden">
      <SEO 
        title="Powerful Features for Modern Business - Gio Billing"
        description="Explore GST invoicing, multi-outlet management, and real-time inventory tracking features of Gio Billing."
        url="/features"
      />

      {/* 🔷 Centered Hero Section (Reference Style) */}
      <section className="relative pt-24 pb-20 px-6 md:px-10 lg:px-12 overflow-hidden bg-white">
        {/* Subtle Grid Pattern Background */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#10b981 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/50 to-white pointer-events-none"></div>

        <div className="max-w-[1400px] mx-auto relative z-10 text-center">
          <div className="space-y-10">
            {/* Title Block */}
            <div className="space-y-6">
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold text-[#1a1a1a] leading-[1.1] tracking-tight">
                Explore Gio Billing's <br />
                Powerful Features
              </h1>
              <p className="text-[#5a5a5a] text-lg md:text-xl leading-relaxed max-w-3xl mx-auto font-medium opacity-80">
                Discover how Gio Billing's cutting-edge features can transform the way you manage your retail business. 
                We provide everything you need to make smarter, data-driven decisions and optimize your business operations.
              </p>
            </div>

            {/* Centered Button */}
            <div className="flex justify-center pt-4">
              <a 
                href="#features-list" 
                className="bg-[#004d40] text-white px-10 py-5 rounded-xl font-bold text-lg hover:bg-[#00382d] transition-all shadow-2xl shadow-emerald-900/20 flex items-center gap-3 active:scale-95 group"
              >
                Start Exploring Now <ArrowRight size={20} className="rotate-90 group-hover:translate-y-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>


      {/* Feature Sections */}
      <div id="features-list">
        <FeatureSection 
        title="Stay 100% Compliant with Automated GST Reports"
        description="Forget manual tax calculations. Gio Billing automatically handles HSN/SAC codes, multiple tax slabs, and generates GSTR-ready reports in a single click."
        image="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80"
        bgColor="bg-white"
      />

      <FeatureSection 
        title="Manage Multiple Outlets from One Dashboard"
        description="Whether you have one shop or a hundred, our cloud-based system keeps your data synced. Monitor performance and transfer stock between branches effortlessly."
        image="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80"
        reversed={true}
        bgColor="bg-[#ecfdf5]"
      />

      <FeatureSection 
        title="Staff Control & Fraud Prevention"
        description="Assign specific roles and permissions to your staff. Track every action with detailed audit logs to ensure maximum security and transparency in your business."
        image="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
        bgColor="bg-white"
      />

      <FeatureSection 
        title="Real-time Inventory & Stock Tracking"
        description="Never run out of stock again. Gio Billing tracks your inventory across all outlets, sends low-stock alerts, and manages inward workflows seamlessly."
        image="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80"
        reversed={true}
        bgColor="bg-[#ecfdf5]"
      />

        <FeatureSection 
          title="Digital Invoicing & Seamless Payments"
          description="Go paperless and get paid faster. Send professional digital invoices via WhatsApp/Email and accept instant payments using QR codes integrated directly into your checkout."
          image="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80"
          bgColor="bg-white"
        />
      </div>


      {/* Testimonials Carousel Section */}
      <section className="py-12 bg-white px-6 md:px-10 lg:px-12 overflow-hidden">
        <div className="max-w-[1400px] mx-auto text-center space-y-3 mb-12">
          <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-widest">Testimonials</h4>
          <h2 className="text-3xl md:text-5xl font-bold text-[#1a1a1a]">What Our Client Say!</h2>
        </div>

        <div className="max-w-[1400px] mx-auto relative flex items-center gap-4">
          <button className="hidden md:flex absolute left-0 z-20 w-12 h-12 rounded-full bg-white shadow-lg items-center justify-center text-gray-400 hover:text-emerald-500 transition-all">
            <ChevronLeft size={24} />
          </button>
          <button className="hidden md:flex absolute right-0 z-20 w-12 h-12 rounded-full bg-white shadow-lg items-center justify-center text-gray-400 hover:text-emerald-500 transition-all">
            <ChevronRight size={24} />
          </button>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch w-full">
            {loading ? (
              <div className="col-span-3 flex justify-center py-12">
                <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
              </div>
            ) : testimonials.length > 0 ? (
              testimonials.slice(0, 3).map((t, idx) => (
                <TestimonialCard 
                  key={t.id} 
                  name={t.client_name}
                  location={t.location}
                  content={t.message}
                  isFeatured={idx === 1} 
                />
              ))
            ) : (
              <div className="col-span-3 py-12 text-gray-400 text-center italic">No testimonials available yet.</div>
            )}
          </div>
        </div>

        <div className="flex justify-center gap-2 mt-12">
          {[0, 1, 2].map(i => (
            <div key={i} className={`w-2 h-2 rounded-full ${i === 1 ? 'bg-emerald-500 w-4' : 'bg-emerald-200'}`}></div>
          ))}
        </div>
      </section>

    </div>
  );
};

export default FeaturesPage;
